import { API_URL, ENDPOINTS } from './constants';
import { getDateFromInputString } from './global';
export const ABORT_ERROR = 'REQUEST_ABORTED';

/** Relative in-app paths only — blocks open redirects (e.g. //evil.com). */
export function getSafeReturnPath(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const path = raw.trim();
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/login')) return null;
  return path;
}

async function clearSessionCookieAndReload() {
  try {
    await fetch(API_URL + ENDPOINTS.LOGOUT, {
      method: 'POST',
      credentials: 'include'
    });
  } catch (_) { }
  setTimeout(() => {
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    localStorage.clear();

    if (window.location.pathname === '/login') {
      window.location.reload();
      return;
    }

    const safeReturn = getSafeReturnPath(returnTo);
    window.location.assign(safeReturn ? `/login?redirect=${encodeURIComponent(safeReturn)}` : '/login');
  }, 1500);
}

const replaceDatesInJson = obj => {
  if (Array.isArray(obj)) {
    return obj.map(item => replaceDatesInJson(item));
  } else if (typeof obj === 'object' && obj !== null && !(obj instanceof Date)) {
    return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, replaceDatesInJson(value)]));
  } else if (typeof obj === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(obj)) {
    return getDateFromInputString(obj);
  }
  return obj;
};

export const makeApiRequests = async ({
  endpoint,
  requestBody,
  contentType = 'application/json',
  method = 'POST',
  stringify = true,
  signal,
  authSilent = false
}) => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const headers = {
    'X-Time-Zone': timeZone
  };

  let dateFormattedRequestBody = requestBody;
  if (![ENDPOINTS.FILE, ENDPOINTS.DISK_IMAGE_UPLOAD, ENDPOINTS.DISK_VIDEO_UPLOAD].includes(endpoint)) {
    headers['Content-Type'] = contentType;
    dateFormattedRequestBody = requestBody ? replaceDatesInJson(requestBody) : undefined;
  }

  try {
    const response = await fetch(API_URL + endpoint, {
      method: method,
      body: stringify && dateFormattedRequestBody ? JSON.stringify(dateFormattedRequestBody) : dateFormattedRequestBody,
      headers,
      credentials: 'include',
      signal
    });

    const text = await response.text();
    let responseBody = {};
    if (text) {
      try {
        responseBody = JSON.parse(text);
      } catch {
        responseBody = {};
      }
    }

    if (response.status < 200 || response.status >= 300) {
      if (response.status === 401) {
        if (authSilent) {
          return { response: null };
        }
        await clearSessionCookieAndReload();
        return { error: 'Your session has expired, please try logging in again!' };
      }
      return { error: responseBody['message'] || 'Oops something went wrong!' };
    } else {
      return { response: responseBody };
    }
  } catch (e) {
    if (e.name === 'AbortError') {
      return { error: ABORT_ERROR };
    }

    console.log(e);
    return { error: 'Oops something went wrong!' };
  }
};

/** Multipart upload to course attachments; does not use JSON Content-Type. */
export async function uploadCourseAttachment(courseId, file) {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(API_URL + ENDPOINTS.COURSE_ATTACHMENT_UPLOAD(courseId), {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });
    const text = await res.text();
    let body = {};
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        body = {};
      }
    }
    if (res.status < 200 || res.status >= 300) {
      if (res.status === 401) {
        await clearSessionCookieAndReload();
        return { error: 'Your session has expired, please try logging in again!' };
      }
      return { error: body.message || 'Upload failed' };
    }
    const fileId = body._id != null ? String(body._id) : body.id != null ? String(body.id) : null;
    return { response: { ...body, fileId } };
  } catch (e) {
    console.log(e);
    return { error: 'Upload failed' };
  }
}

/** Multipart upload of a file into a file explorer folder; does not use JSON Content-Type. */
export async function uploadFileExplorerFile({ parentFolderName, parentId, file }) {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('parentFolderName', parentFolderName);
    if (parentId) {
      formData.append('parentId', parentId);
    }
    const res = await fetch(API_URL + ENDPOINTS.FILE_EXPLORER_FILE, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });
    const text = await res.text();
    let body = {};
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        body = {};
      }
    }
    if (res.status < 200 || res.status >= 300) {
      if (res.status === 401) {
        await clearSessionCookieAndReload();
        return { error: 'Your session has expired, please try logging in again!' };
      }
      return { error: body.message || body.error || 'Upload failed' };
    }
    return { response: body };
  } catch (e) {
    console.log(e);
    return { error: 'Upload failed' };
  }
}

function resolveReadFileUrl({ bucket, fileName, courseId, fileId, nodeId }) {
  if (bucket === 'fileExplorer') {
    if (nodeId == null || String(nodeId) === '') {
      return null;
    }
    return API_URL + ENDPOINTS.FILE_EXPLORER_DOWNLOAD(nodeId);
  }
  if (bucket === 'course') {
    if (courseId == null || fileId == null || String(courseId) === '' || String(fileId) === '') {
      return null;
    }
    return API_URL + ENDPOINTS.COURSE_ATTACHMENT_STREAM(courseId, fileId);
  }
  if (fileName == null || String(fileName) === '') {
    return null;
  }
  return API_URL + ENDPOINTS.FILE_STREAM_WITH_ID(fileName);
}

export function parseFilenameFromContentDisposition(cd) {
  if (!cd || typeof cd !== 'string') return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star && star[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      return star[1].trim();
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(cd);
  if (quoted && quoted[1]) return quoted[1].trim();
  const plain = /filename=([^;\s]+)/i.exec(cd);
  if (plain && plain[1]) return plain[1].replace(/^["']|["']$/g, '').trim();
  return null;
}

export const readFile = async ({
  fileName,
  mimeType,
  bucket = 'file',
  courseId,
  fileId,
  nodeId,
  parseContentDisposition = false
}) => {
  try {
    const url = resolveReadFileUrl({ bucket, fileName, courseId, fileId, nodeId });
    if (!url) {
      return { error: 'Could not load file!' };
    }

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include'
    });

    if (response.status < 200 || response.status >= 300) {
      if (response.status === 401) {
        await clearSessionCookieAndReload();
        return { error: 'Your session has expired, please try logging in again!' };
      }
      return { error: 'Could not load file!' };
    }

    const blobFromResponse = await response.blob();
    const blobForUrl =
      mimeType != null && String(mimeType) !== '' ? new Blob([blobFromResponse], { type: mimeType }) : blobFromResponse;
    const returnValue = URL.createObjectURL(blobForUrl);

    if (parseContentDisposition) {
      const cd = response.headers.get('Content-Disposition');
      const suggestedFilename = parseFilenameFromContentDisposition(cd);
      const contentType = response.headers.get('Content-Type');
      return { response: returnValue, suggestedFilename, contentType };
    }

    return { response: returnValue };
  } catch (e) {
    console.log(e);
    return { error: 'Oops something went wrong!' };
  }
};

export const deleteFileFromServer = file => {
  //we do not care for response
  makeApiRequests({
    endpoint: ENDPOINTS.FILE_WITH_ID(file.filename),
    method: 'DELETE'
  });
};

export const getCoordinatesOfAddress = async address => {
  try {
    const geocodeResponse = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${process.env.REACT_APP_MAP_KEY
      }`
    );

    if (!geocodeResponse.ok) {
      return;
    }

    const geocodeData = await geocodeResponse.json();

    if (geocodeData.results.length > 0) {
      return geocodeData.results[0].geometry.location;
    }
  } catch (geocodeError) { }
};

const blobToBase64 = blob => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  return new Promise(resolve => {
    reader.onloadend = () => {
      resolve(reader.result);
    };
  });
};
